/**
 * Structural operations: tracks, transitions, sequences (groups/chapters), markers, media bin and
 * project settings.
 */
import { cloneData } from '../model/clone.ts';
import { createSequence, createSequenceClip, createTrack } from '../model/factory.ts';
import { newId } from '../model/ids.ts';
import { framesToSeconds, secondsToNearestFrame } from '../model/time.ts';
import type {
  Clip,
  Id,
  MediaAsset,
  MediaFolder,
  Project,
  ProjectSettings,
  Track,
  TrackKind,
  Transition,
  TransitionType,
} from '../model/schema.ts';
import {
  allTracks,
  clipEnd,
  getClip,
  getSequence,
  getTrack,
  mediaUsageCount,
  sequenceUsages,
} from '../timeline/queries.ts';
import { assertEditable, insertClip, setClipHead } from './clip-ops.ts';
import { EditError, invariant } from './errors.ts';
import { normalizeProject, normalizeTrack } from './normalize.ts';

// ---------------------------------------------------------------------------------------------
// Tracks
// ---------------------------------------------------------------------------------------------

function tracksOf(project: Project, sequenceId: Id, kind: TrackKind): Track[] {
  const seq = getSequence(project, sequenceId);
  return kind === 'video' ? seq.videoTracks : seq.audioTracks;
}

export function addTrack(project: Project, sequenceId: Id, kind: TrackKind, index?: number): Id {
  const list = tracksOf(project, sequenceId, kind);
  const prefix = kind === 'video' ? 'V' : 'A';
  const track = createTrack(kind, `${prefix}${list.length + 1}`);
  list.splice(index ?? list.length, 0, track);
  return track.id;
}

export function removeTrack(project: Project, trackId: Id): void {
  const { sequence, track } = getTrack(project, trackId);
  const list = track.kind === 'video' ? sequence.videoTracks : sequence.audioTracks;
  invariant(list.length > 1, `A sequence needs at least one ${track.kind} track`);
  list.splice(list.indexOf(track), 1);
}

export type TrackPatch = Partial<Pick<Track, 'name' | 'muted' | 'solo' | 'locked' | 'hidden' | 'volume'>>;

export function updateTrack(project: Project, trackId: Id, patch: TrackPatch): void {
  const { track } = getTrack(project, trackId);
  if (patch.volume !== undefined) invariant(patch.volume >= 0 && patch.volume <= 4, 'Volume out of range');
  Object.assign(track, patch);
}

export function moveTrack(project: Project, trackId: Id, newIndex: number): void {
  const { sequence, track } = getTrack(project, trackId);
  const list = track.kind === 'video' ? sequence.videoTracks : sequence.audioTracks;
  list.splice(list.indexOf(track), 1);
  list.splice(Math.max(0, Math.min(list.length, newIndex)), 0, track);
}

// ---------------------------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------------------------

/**
 * Adds a transition on the cut after `fromClipId`. The next clip on the track must start exactly
 * where `fromClipId` ends. Replaces an existing transition on that cut.
 */
export function addTransition(project: Project, fromClipId: Id, type: TransitionType, duration: number): Id {
  const { track, clip, index } = getClip(project, fromClipId);
  assertEditable(track);
  const next = track.clips[index + 1];
  invariant(next && next.start === clipEnd(clip), 'Transitions need two clips touching each other');
  track.transitions = track.transitions.filter((t) => t.fromClipId !== clip.id);
  const tr: Transition = {
    id: newId('trn'),
    type,
    duration: Math.max(1, Math.round(duration)),
    fromClipId: clip.id,
    toClipId: next.id,
  };
  track.transitions.push(tr);
  normalizeTrack(track);
  return tr.id;
}

function findTransition(project: Project, transitionId: Id): { track: Track; tr: Transition } {
  for (const seq of Object.values(project.sequences)) {
    for (const track of allTracks(seq)) {
      const tr = track.transitions.find((t) => t.id === transitionId);
      if (tr) return { track, tr };
    }
  }
  throw new EditError('Transition not found');
}

export function updateTransition(
  project: Project,
  transitionId: Id,
  patch: { type?: TransitionType; duration?: number },
): void {
  const { track, tr } = findTransition(project, transitionId);
  assertEditable(track);
  if (patch.type) tr.type = patch.type;
  if (patch.duration !== undefined) tr.duration = Math.max(1, Math.round(patch.duration));
  normalizeTrack(track);
}

export function removeTransition(project: Project, transitionId: Id): void {
  const { track, tr } = findTransition(project, transitionId);
  assertEditable(track);
  track.transitions.splice(track.transitions.indexOf(tr), 1);
}

// ---------------------------------------------------------------------------------------------
// Sequences ("groups" / chapters)
// ---------------------------------------------------------------------------------------------

export function addSequence(project: Project, name: string): Id {
  const seq = createSequence(name);
  project.sequences[seq.id] = seq;
  return seq.id;
}

export function renameSequence(project: Project, sequenceId: Id, name: string): void {
  getSequence(project, sequenceId).name = name;
  for (const { clip } of sequenceUsages(project, sequenceId)) {
    if (clip.type === 'sequence') clip.name = name;
  }
}

export function deleteSequence(project: Project, sequenceId: Id): void {
  invariant(sequenceId !== project.rootSequenceId, 'The main sequence cannot be deleted');
  invariant(sequenceUsages(project, sequenceId).length === 0, 'Sequence is still used in another timeline');
  getSequence(project, sequenceId);
  delete project.sequences[sequenceId];
}

/**
 * Groups clips into a new nested sequence and replaces them with a single sequence clip
 * (placed on the lowest video track of the selection, or the first video track for audio-only
 * selections). Relative timing and track layout are preserved inside the group.
 * Returns the new sequence id.
 */
export function groupClips(project: Project, clipIds: Id[], name: string): Id {
  invariant(clipIds.length > 0, 'Select at least one clip to group');
  const locs = clipIds.map((id) => getClip(project, id));
  const parent = locs[0]!.sequence;
  invariant(
    locs.every((l) => l.sequence.id === parent.id),
    'Clips must be in the same sequence',
  );
  for (const l of locs) assertEditable(l.track);
  const start = Math.min(...locs.map((l) => l.clip.start));
  const end = Math.max(...locs.map((l) => clipEnd(l.clip)));

  const child = createSequence(name);
  child.videoTracks = parent.videoTracks.map((t) => ({ ...createTrack('video', t.name) }));
  child.audioTracks = parent.audioTracks.map((t) => ({ ...createTrack('audio', t.name) }));
  project.sequences[child.id] = child;

  const ids = new Set(clipIds);
  for (const kind of ['videoTracks', 'audioTracks'] as const) {
    parent[kind].forEach((track, i) => {
      const moving = track.clips.filter((c) => ids.has(c.id));
      if (moving.length === 0) return;
      const target = child[kind][i]!;
      target.transitions = track.transitions.filter((t) => ids.has(t.fromClipId) && ids.has(t.toClipId));
      track.clips = track.clips.filter((c) => !ids.has(c.id));
      for (const c of moving) {
        // Copy (not spread): a spread of a draft would embed draft proxies in a plain object.
        const copy = cloneData(c);
        copy.start = c.start - start;
        target.clips.push(copy);
      }
      normalizeTrack(target);
      normalizeTrack(track);
    });
  }
  // Drop empty trailing tracks in the child but keep at least one of each kind.
  for (const kind of ['videoTracks', 'audioTracks'] as const) {
    while (child[kind].length > 1 && child[kind][child[kind].length - 1]!.clips.length === 0)
      child[kind].pop();
  }

  const videoLocs = locs.filter((l) => l.track.kind === 'video');
  const hostTrack =
    videoLocs.length > 0
      ? parent.videoTracks[Math.min(...videoLocs.map((l) => parent.videoTracks.indexOf(l.track)))]!
      : parent.videoTracks[0]!;
  insertClip(project, hostTrack.id, createSequenceClip(child, start, end - start), 'overwrite');
  return child.id;
}

/**
 * Expands a sequence clip back into its parent timeline (the inverse of grouping). Only the
 * visible window of the nested sequence is restored; the group's own transform is discarded.
 */
export function ungroupClip(project: Project, clipId: Id): Id[] {
  const { sequence: parent, track, clip } = getClip(project, clipId);
  invariant(clip.type === 'sequence', 'Only group clips can be ungrouped');
  assertEditable(track);
  const child = getSequence(project, clip.sequenceId);
  const windowStart = clip.sourceIn;
  const windowEnd = clip.sourceIn + clip.duration;
  const offset = clip.start - windowStart;
  track.clips.splice(track.clips.indexOf(clip), 1);
  normalizeTrack(track);

  const created: Id[] = [];
  const baseVideo = parent.videoTracks.indexOf(track);
  for (const kind of ['videoTracks', 'audioTracks'] as const) {
    child[kind].forEach((childTrack, i) => {
      const targetIndex = kind === 'videoTracks' ? baseVideo + i : i;
      while (parent[kind].length <= targetIndex) {
        parent[kind].push(
          createTrack(
            kind === 'videoTracks' ? 'video' : 'audio',
            `${kind[0]!.toUpperCase()}${parent[kind].length + 1}`,
          ),
        );
      }
      const target = parent[kind][targetIndex]!;
      const idMap = new Map<Id, Id>();
      for (const c of childTrack.clips) {
        if (clipEnd(c) <= windowStart || c.start >= windowEnd) continue;
        const copy = cloneData(c) as Clip;
        copy.id = newId('clp');
        copy.start = c.start + offset;
        // Trim to the window that was visible through the group clip *before* placing it, so
        // nothing outside that window is overwritten.
        const visStart = windowStart + offset;
        const visEnd = windowEnd + offset;
        if (clipEnd(copy) > visEnd) copy.duration = visEnd - copy.start;
        if (copy.start < visStart) setClipHead(copy, visStart, project);
        const placedId = insertClip(project, target.id, copy, 'overwrite').id;
        idMap.set(c.id, placedId);
        created.push(placedId);
      }
      for (const tr of childTrack.transitions) {
        const fromClipId = idMap.get(tr.fromClipId);
        const toClipId = idMap.get(tr.toClipId);
        if (fromClipId && toClipId)
          target.transitions.push({ ...tr, id: newId('trn'), fromClipId, toClipId });
      }
      normalizeTrack(target);
    });
  }
  if (sequenceUsages(project, child.id).length === 0) delete project.sequences[child.id];
  return created;
}

// ---------------------------------------------------------------------------------------------
// Markers
// ---------------------------------------------------------------------------------------------

export function addMarker(
  project: Project,
  sequenceId: Id,
  frame: number,
  label = '',
  color = '#f5a524',
): Id {
  const seq = getSequence(project, sequenceId);
  const id = newId('mrk');
  seq.markers.push({ id, frame: Math.max(0, Math.round(frame)), label, color });
  seq.markers.sort((a, b) => a.frame - b.frame);
  return id;
}

export function removeMarker(project: Project, sequenceId: Id, markerId: Id): void {
  const seq = getSequence(project, sequenceId);
  seq.markers = seq.markers.filter((m) => m.id !== markerId);
}

// ---------------------------------------------------------------------------------------------
// Media bin
// ---------------------------------------------------------------------------------------------

export function addMedia(project: Project, asset: MediaAsset): void {
  project.media[asset.id] = asset;
  if (!project.mediaOrder.includes(asset.id)) project.mediaOrder.push(asset.id);
}

export function renameMedia(project: Project, mediaId: Id, name: string): void {
  const media = project.media[mediaId];
  invariant(media, 'Unknown media');
  media.name = name;
}

/** Removes media from the bin. With `force`, clips using it are deleted as well. */
export function removeMedia(project: Project, mediaId: Id, force = false): void {
  const uses = mediaUsageCount(project, mediaId);
  invariant(force || uses === 0, `Media is used by ${uses} clip(s)`);
  for (const seq of Object.values(project.sequences)) {
    for (const track of allTracks(seq)) {
      track.clips = track.clips.filter((c) => !('mediaId' in c) || c.mediaId !== mediaId);
      normalizeTrack(track);
    }
  }
  delete project.media[mediaId];
  project.mediaOrder = project.mediaOrder.filter((id) => id !== mediaId);
}

/**
 * Swaps a library item for a newly imported one (e.g. an updated version of the same file):
 * every clip using `oldId` now uses `asset`, which takes the old item's place and folder.
 */
export function replaceMedia(project: Project, oldId: Id, asset: MediaAsset): void {
  const old = project.media[oldId];
  invariant(old, 'Unknown media');
  invariant(asset.id === oldId || !project.media[asset.id], 'Media already in the project');
  invariant(
    old.kind === asset.kind,
    `“${old.name}” is ${old.kind === 'image' ? 'an image' : `a ${old.kind} file`} and can only be replaced by one`,
  );
  for (const seq of Object.values(project.sequences)) {
    for (const track of allTracks(seq)) {
      for (const clip of track.clips)
        if ('mediaId' in clip && clip.mediaId === oldId) clip.mediaId = asset.id;
    }
  }
  const next: MediaAsset = { ...asset };
  if (old.folderId !== undefined) next.folderId = old.folderId;
  else delete next.folderId;
  delete project.media[oldId];
  project.media[next.id] = next;
  project.mediaOrder = project.mediaOrder
    .filter((id) => id !== next.id)
    .map((id) => (id === oldId ? next.id : id));
}

// ---------------------------------------------------------------------------------------------
// Library folders
// ---------------------------------------------------------------------------------------------

function assertFolder(project: Project, folderId: Id | undefined): void {
  invariant(folderId === undefined || project.mediaFolders[folderId], 'Unknown folder');
}

/** The folder and its ancestors, innermost first (empty for the top level). */
export function mediaFolderPath(project: Project, folderId: Id | undefined): MediaFolder[] {
  const path: MediaFolder[] = [];
  const seen = new Set<Id>();
  for (let id = folderId; id !== undefined && !seen.has(id);) {
    seen.add(id);
    const folder = project.mediaFolders[id];
    if (!folder) break;
    path.push(folder);
    id = folder.parentId;
  }
  return path;
}

/** Creates a library folder and returns its id. */
export function createMediaFolder(project: Project, name: string, parentId?: Id): Id {
  assertFolder(project, parentId);
  const folder: MediaFolder = { id: newId('fld'), name: name.trim() || 'Folder' };
  if (parentId !== undefined) folder.parentId = parentId;
  project.mediaFolders[folder.id] = folder;
  return folder.id;
}

/**
 * Returns the folder named `name` directly inside `parentId`, creating it if needed. Used to mirror
 * dropped directories without duplicating folders on repeated imports.
 */
export function ensureMediaFolder(project: Project, name: string, parentId?: Id): Id {
  const existing = Object.values(project.mediaFolders).find(
    (f) => f.parentId === parentId && f.name === name,
  );
  return existing ? existing.id : createMediaFolder(project, name, parentId);
}

export function renameMediaFolder(project: Project, folderId: Id, name: string): void {
  const folder = project.mediaFolders[folderId];
  invariant(folder, 'Unknown folder');
  invariant(name.trim(), 'A folder needs a name');
  folder.name = name.trim();
}

/** Moves a folder into another one (`undefined` ⇒ top level). Rejects moving a folder into itself. */
export function moveMediaFolder(project: Project, folderId: Id, parentId: Id | undefined): void {
  const folder = project.mediaFolders[folderId];
  invariant(folder, 'Unknown folder');
  assertFolder(project, parentId);
  invariant(
    !mediaFolderPath(project, parentId).some((f) => f.id === folderId),
    'A folder cannot be moved into itself',
  );
  if (parentId === undefined) delete folder.parentId;
  else folder.parentId = parentId;
}

/** Files media into a folder (`undefined` ⇒ top level). */
export function moveMediaToFolder(project: Project, mediaIds: Id[], folderId: Id | undefined): void {
  assertFolder(project, folderId);
  for (const id of mediaIds) {
    const media = project.media[id];
    invariant(media, 'Unknown media');
    if (folderId === undefined) delete media.folderId;
    else media.folderId = folderId;
  }
}

/**
 * Deletes a folder. Its media and sub-folders move up to the folder's parent, so nothing is
 * removed from the project.
 */
export function deleteMediaFolder(project: Project, folderId: Id): void {
  const folder = project.mediaFolders[folderId];
  invariant(folder, 'Unknown folder');
  const parentId = folder.parentId;
  for (const child of Object.values(project.mediaFolders)) {
    if (child.parentId !== folderId) continue;
    if (parentId === undefined) delete child.parentId;
    else child.parentId = parentId;
  }
  for (const media of Object.values(project.media)) {
    if (media.folderId !== folderId) continue;
    if (parentId === undefined) delete media.folderId;
    else media.folderId = parentId;
  }
  delete project.mediaFolders[folderId];
}

// ---------------------------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------------------------

/**
 * Updates project settings. Changing the frame rate re-times every frame-based value so that
 * content keeps its real-time position.
 */
export function updateSettings(project: Project, patch: Partial<ProjectSettings>): void {
  const old = project.settings;
  const next = { ...old, ...patch };
  invariant(
    next.width % 2 === 0 && next.height % 2 === 0,
    'Width and height must be even (H.264 requirement)',
  );
  const fpsChanged = next.fps.num * old.fps.den !== old.fps.num * next.fps.den;
  project.settings = next;
  if (!fpsChanged) return;
  const conv = (f: number) => secondsToNearestFrame(framesToSeconds(f, old.fps), next.fps);
  for (const seq of Object.values(project.sequences)) {
    for (const m of seq.markers) m.frame = conv(m.frame);
    for (const track of allTracks(seq)) {
      for (const c of track.clips) {
        const end = conv(c.start + c.duration);
        c.start = conv(c.start);
        c.duration = Math.max(1, end - c.start);
        if (c.type === 'sequence') c.sourceIn = conv(c.sourceIn);
        for (const anim of Object.values(c.params))
          for (const k of anim?.keyframes ?? []) k.frame = conv(k.frame);
      }
      for (const t of track.transitions) t.duration = Math.max(1, conv(t.duration));
    }
  }
  normalizeProject(project);
}

export function renameProject(project: Project, name: string): void {
  invariant(name.trim().length > 0, 'Name must not be empty');
  project.name = name.trim();
}
